/**
 * Collection cards carousel.
 *
 * A horizontally scrollable row of category cards. Scrolling itself is native
 * (CSS scroll-snap), so the section works without JavaScript; this element only
 * adds the previous/next controls and their state.
 *
 * Behaviour:
 * - Steps by exactly one card (card width + gap) per click.
 * - Disables an arrow when that end of the track is reached.
 * - Hides both arrows entirely when the track does not overflow.
 * - Direction-aware: handles right-to-left storefronts, where `scrollLeft`
 *   runs from 0 down to a negative value.
 */
export class CollectionCards extends HTMLElement {
  /** @type {HTMLElement|null} */
  #scroller = null;

  /** @type {HTMLButtonElement|null} */
  #previous = null;

  /** @type {HTMLButtonElement|null} */
  #next = null;

  /** @type {ResizeObserver|undefined} */
  #resizeObserver = undefined;

  connectedCallback() {
    this.#scroller = this.querySelector('[data-cc-scroller]');
    this.#previous = this.querySelector('[data-cc-prev]');
    this.#next = this.querySelector('[data-cc-next]');

    if (!this.#scroller) return;

    this.#scroller.addEventListener('scroll', this.#sync, { passive: true });
    this.#previous?.addEventListener('click', this.#goPrevious);
    this.#next?.addEventListener('click', this.#goNext);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#sync);
      this.#resizeObserver.observe(this.#scroller);
    } else {
      window.addEventListener('resize', this.#sync);
    }

    this.#sync();
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#sync);
    this.#previous?.removeEventListener('click', this.#goPrevious);
    this.#next?.removeEventListener('click', this.#goNext);

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;

    window.removeEventListener('resize', this.#sync);
  }

  /**
   * Distance of a single card, including the gap that follows it.
   *
   * @returns {number}
   */
  get #step() {
    const scroller = this.#scroller;

    if (!scroller) return 0;

    const card = scroller.querySelector('[data-cc-item]');

    if (!card) return scroller.clientWidth;

    const styles = getComputedStyle(scroller.querySelector('.collection-cards__track') ?? scroller);
    const gap = parseFloat(styles.columnGap) || 0;

    return card.getBoundingClientRect().width + gap;
  }

  /**
   * `true` when the storefront reads right-to-left.
   *
   * @returns {boolean}
   */
  get #isRightToLeft() {
    return getComputedStyle(this).direction === 'rtl';
  }

  /**
   * @returns {ScrollBehavior}
   */
  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * Scrolls one card forward (1) or backward (-1).
   *
   * @param {number} direction
   */
  #scrollByCard(direction) {
    const scroller = this.#scroller;

    if (!scroller) return;

    const sign = this.#isRightToLeft ? -1 : 1;

    scroller.scrollBy({
      left: this.#step * direction * sign,
      behavior: this.#behavior,
    });
  }

  #goPrevious = () => this.#scrollByCard(-1);

  #goNext = () => this.#scrollByCard(1);

  /**
   * Reflects the current scroll position on the controls.
   */
  #sync = () => {
    const scroller = this.#scroller;

    if (!scroller) return;

    const maximum = scroller.scrollWidth - scroller.clientWidth;

    /* In RTL, scrollLeft counts down from 0 into negative numbers. */
    const position = Math.abs(scroller.scrollLeft);

    /* Sub-pixel layout rounding means the ends are never exactly hit. */
    const threshold = 2;
    const overflows = maximum > threshold;

    this.toggleAttribute('data-cc-static', !overflows);

    if (this.#previous) {
      this.#previous.disabled = !overflows || position <= threshold;
    }

    if (this.#next) {
      this.#next.disabled = !overflows || position >= maximum - threshold;
    }
  };
}

if (!customElements.get('collection-cards-component')) {
  customElements.define('collection-cards-component', CollectionCards);
}
